export enum ToastType {
    Info = 'INFO',
    Success = 'SUCCESS',
    Error = 'ERROR',
    Pending = 'PENDING',
}

export interface Toast {
    message: string,
    type: ToastType,
    autoRemove: boolean,
    link?: {
        text: string,
        url: string
    }
}

class ErrorToast implements Toast {
    type = ToastType.Error;
    autoRemove = false

    constructor(public readonly message: string) {}
}

class SuccessToast implements Toast {
    type = ToastType.Success

    constructor(
        public readonly message: string,
        public readonly autoRemove: boolean = true
    ) {}
}

class PendingToast implements Toast {
    type = ToastType.Pending;
    autoRemove = false

    constructor(public message: string,) {}
}

const upgradeToast: Toast = {
    message: `<div>Go <b>Pro</b> to save & delete words</div>`,
    type: ToastType.Info,
    autoRemove: true,
    link: {
        text: '» Upgrade',
        url: '/pricing'
    }
}

const errorLoadingPricesToast = {
    message: '<p><b>Oops! We\'re having trouble loading the prices.</b> Our team has been notified, and we\'re on it! Please try refreshing the page or check back soon.</p><p>If the issue persists, feel free to reach out to us at <u>support@vocabeo.com</u> - we\'re happy to help!</p>',
    type: ToastType.Error,
    autoRemove: false,
}

const errorLoadingMobileSubscriptionPricesToast = {
    message: `Oops! We couldn't load the prices. Please try again in a moment.`,
    type: ToastType.Error,
    autoRemove: false,
}

const errorPurchasingMobileSubscriptionToast = {
    message: `<p>We couldn’t complete your subscription purchase. Please try again.</p><p>If the issue persists, feel free to reach out to us at <u>support@vocabeo.com</u> - we're happy to help!</p>`,
    type: ToastType.Error,
    autoRemove: false,
}

// noinspection JSUnusedLocalSymbols
const exampleToasts = [
    {
        message: 'This is an info toast',
        type: ToastType.Info,
        autoRemove: true,
    },
    {
        message: 'This is a success toast',
        type: ToastType.Success,
        autoRemove: true,
    },
    {
        message: 'This is an error toast',
        type: ToastType.Error,
        autoRemove: false,
    }
]

export class ToastManager {

    // NOTE with $state.raw no proxy is created = contained elements can be compared which is needed for addUpgradeToast
    //  State declared with $state.raw cannot be mutated; it can only be reassigned.
    _toasts: Toast[] = $state.raw([
        // ...exampleToasts
        // upgradeToast,
        // errorLoadingPricesToast
        // errorLoadingMobileSubscriptionPricesToast
        // errorPurchasingMobileSubscriptionToast
    ])

    public get toasts() {
        return this._toasts
    }

    constructor() { }

    private addToast(toast: Toast) {
        this._toasts = [...this._toasts, toast] //assign because of $state.raw
    }

    private replaceToast(existingToast: Toast, newToast: Toast) {
        const index = this._toasts.indexOf(existingToast)
        if (index === -1) {
            console.error('ERROR toast to replace not found')
            return
        }
        this._toasts[index] = newToast
        this._toasts = [...this._toasts] //reassign to trigger update because of $state.raw
    }

    addUpgradeToast() {
        if (this._toasts.includes(upgradeToast)) return
        this.addToast(upgradeToast)
    }

    addErrorLoadingPricesToast() {
        this.addToast(errorLoadingPricesToast)
    }

    addErrorLoadingMobileSubscriptionPricesToast() {
        this.addToast(errorLoadingMobileSubscriptionPricesToast)
    }

    addErrorPurchasingMobileSubscriptionToast() {
        this.addToast(errorPurchasingMobileSubscriptionToast)
    }

    addResetWordsInProgressToast() {
        const pendingToast = new PendingToast('Resetting words... Hang tight, this might take a moment.')
        this.addToast(pendingToast)
        return {
            resolve: () => {
                this.replaceToast(pendingToast, new SuccessToast('Your words have been successfully reset.', false))
            },
            reject: () => {
                this.replaceToast(pendingToast, new ErrorToast('Something went wrong while resetting your words. Please try again.'))
            }
        }
    }

    addSuccessToast(message: string, autoRemove: boolean = true) {
        this.addToast(new SuccessToast(message, autoRemove))
    }

    addErrorToast(message: string) {
        this.addToast(new ErrorToast(message))
    }

    removeToast(toast: Toast) {
        this._toasts = this._toasts.filter(t => t !== toast)
    }
}